import { AdminTask, ProductTaskIds, TaskStatus } from 'core/application/adminDashboard/adminTasks'
import { ProductIds } from 'core/application/commons/config'

import { ROUTE_PATHS } from '../common/routePaths'
import ActionNeeded from './actionNeeded'
import BenefitDetails from './benefitDetails'
import CompanySettings from './companySettings'
import AdminDashboardHome from './home'
import LegalDocuments from './legalDocuments'
import ManagePeople from './managePeople'
import Emails from './messages/emails'
import SmsMessages from './messages/sms'
import OurProducts from './ourProducts'
import Reimbursements from './reimbursements'
import Reports from './reports'

const productDisableDescriptions = new Map<number, any>([
  [
    ProductIds.Perks,
    {
      Reimbursements: 'admin-dashboard.left-navigation.perks-reimbursements-tooltip',
      ManagePeople: 'admin-dashboard.left-navigation.perks-manage-people-tooltip',
    },
  ],
  [
    ProductIds.GroupHraId,
    {
      Reimbursements: 'admin-dashboard.left-navigation.reimbursements-tooltip',
      ManagePeople: 'admin-dashboard.left-navigation.manage-people-tooltip',
    },
  ],
  [
    ProductIds.QsehraId,
    {
      Reimbursements: 'admin-dashboard.left-navigation.reimbursements-tooltip',
      ManagePeople: 'admin-dashboard.left-navigation.manage-people-tooltip',
    },
  ],
  [
    ProductIds.IchraId,
    {
      Reimbursements: 'admin-dashboard.left-navigation.reimbursements-tooltip',
      ManagePeople: 'admin-dashboard.left-navigation.manage-people-tooltip',
    },
  ],
])

export interface MenuItem {
  name: string
  key: string
  priority: number
  path?: string
  icon?: string
  disabled?: boolean
  disabledDescription: string
  component?: any
  newTab?: boolean
  children: MenuItem[]
  activeForOtherLinks: string[]
  hasBadge?: boolean
}

export const getNavigationMenu = (productId: number, tasks: AdminTask[], showSmsMessages: boolean): MenuItem[] => [
  {
    name: 'admin-dashboard.left-navigation.benefit-plan',
    key: '1',
    disabled: false,
    disabledDescription: 'admin-dashboard.left-navigation.benefit-plan-tooltip',
    priority: 100,
    activeForOtherLinks: [],
    children: [
      {
        name: 'admin-dashboard.left-navigation.reimbursements',
        key: ROUTE_PATHS.REIMBURSEMENTS,
        priority: 100,
        path: `${ROUTE_PATHS.REIMBURSEMENTS}`,
        component: Reimbursements,
        icon: 'far fa-usd-circle',
        disabled: !ensureDependentTasksAreCompleted(tasks, [ProductTaskIds.get(productId).inviteEmployees]),
        disabledDescription: productDisableDescriptions.get(productId)['Reimbursements'],
        activeForOtherLinks: [],
        children: [],
      },
      {
        name: 'admin-dashboard.left-navigation.manage-people',
        key: ROUTE_PATHS.MANAGE_PEOPLE,
        priority: 200,
        path: ROUTE_PATHS.MANAGE_PEOPLE,
        component: ManagePeople,
        icon: 'far fa-users',
        disabled: !ensureDependentTasksAreCompleted(tasks, [ProductTaskIds.get(productId).benefitDesign]),
        disabledDescription: productDisableDescriptions.get(productId)['ManagePeople'],
        activeForOtherLinks: [],
        children: [],
      },
      {
        name: 'admin-dashboard.left-navigation.benefit-details',
        key: ROUTE_PATHS.BENEFIT_DETAILS_PATH,
        priority: 300,
        path: ROUTE_PATHS.BENEFIT_DETAILS_PATH,
        component: BenefitDetails,
        icon: 'far fa-ballot-check',
        disabled: false,
        disabledDescription: 'admin-dashboard.left-navigation.benefit-details-tooltip',
        activeForOtherLinks: [],
        children: [],
      },
      ...(productId !== ProductIds.Perks
        ? [
            {
              name: 'admin-dashboard.left-navigation.legal-documents',
              key: ROUTE_PATHS.LEGAL_DOCUMENTS,
              priority: 400,
              path: ROUTE_PATHS.LEGAL_DOCUMENTS,
              component: LegalDocuments,
              icon: 'far fa-file-signature',
              disabled: !ensureDependentTasksAreCompleted(tasks, [
                ProductTaskIds.get(productId).completeProfile,
                ProductTaskIds.get(productId).enrollEmployees,
              ]),
              disabledDescription: 'admin-dashboard.left-navigation.legal-documents-tooltip',
              activeForOtherLinks: [],
              children: [],
            },
          ]
        : []),
      {
        name: 'admin-dashboard.left-navigation.reports',
        key: ROUTE_PATHS.REPORTS,
        priority: 100,
        path: ROUTE_PATHS.REPORTS,
        component: Reports,
        icon: 'far fa-file-check',
        disabled: false,
        disabledDescription: 'admin-dashboard.left-navigation.reports',
        activeForOtherLinks: [`${ROUTE_PATHS.REPORTS}/`, `${ROUTE_PATHS.REPORTS}/pcori-reports`],
        children: [],
      },
      {
        name: 'admin-dashboard.left-navigation.messages.title',
        key: ROUTE_PATHS.MESSAGES,
        disabled: false,
        disabledDescription: 'admin-dashboard.left-navigation.messages',
        priority: 100,
        activeForOtherLinks: [`${ROUTE_PATHS.MESSAGES}/`, `${ROUTE_PATHS.MESSAGES}/emails`],
        children: [
          {
            name: 'admin-dashboard.left-navigation.messages.email-messages.title',
            key: `${ROUTE_PATHS.MESSAGES}/emails`,
            priority: 100,
            path: `${ROUTE_PATHS.MESSAGES}/emails`,
            component: Emails,
            hasBadge: false,
            disabled: false,
            disabledDescription: 'admin-dashboard.left-navigation.messages',
            activeForOtherLinks: [],
            children: [],
          },
          ...(showSmsMessages
            ? [
                {
                  name: 'admin-dashboard.left-navigation.messages.sms-messages.title',
                  key: `${ROUTE_PATHS.MESSAGES}/sms-messages`,
                  priority: 120,
                  path: `${ROUTE_PATHS.MESSAGES}/sms-messages`,
                  component: SmsMessages,
                  hasBadge: false,
                  disabled: false,
                  disabledDescription: 'admin-dashboard.left-navigation.messages',
                  activeForOtherLinks: [],
                  children: [],
                },
              ]
            : []),
        ],
      },
    ],
  },
  {
    name: 'admin-dashboard.left-navigation.company',
    key: '100',
    path: '/',
    disabledDescription: 'admin-dashboard.left-navigation.company-tooltip',
    priority: 200,
    activeForOtherLinks: [],
    children: [
      {
        name: 'admin-dashboard.left-navigation.company-settings',
        key: ROUTE_PATHS.COMPANY_SETTINGS,
        priority: 100,
        path: ROUTE_PATHS.COMPANY_SETTINGS,
        component: CompanySettings,
        icon: 'far fa-cog',
        disabled: false,
        disabledDescription: 'admin-dashboard.left-navigation.company-settings-tooltip',
        activeForOtherLinks: [
          `${ROUTE_PATHS.COMPANY_SETTINGS}/`,
          `${ROUTE_PATHS.COMPANY_SETTINGS}/admins`,
          `${ROUTE_PATHS.COMPANY_SETTINGS}/subscription`,
        ],
        children: [],
      },

      {
        name: 'admin-dashboard.left-navigation.our-products',
        key: ROUTE_PATHS.OUR_PRODUCTS,
        priority: 100,
        path: ROUTE_PATHS.OUR_PRODUCTS,
        component: OurProducts,
        hasBadge: false,
        icon: 'far fa-cubes',
        disabled: false,
        disabledDescription: 'admin-dashboard.left-navigation.our-products',
        activeForOtherLinks: [`${ROUTE_PATHS.COMPANY_SETTINGS}/admins`, `${ROUTE_PATHS.COMPANY_SETTINGS}/subscription`],
        children: [],
      },

      {
        name: 'admin-dashboard.left-navigation.refer-a-friend',
        key: 'refer-a-friend',
        priority: 200,
        path: `https://www.peoplekeep.com/refer-a-friend`,
        newTab: true,
        icon: 'far fa-megaphone',
        disabled: false,
        disabledDescription: 'admin-dashboard.left-navigation.company-settings-tooltip',
        activeForOtherLinks: [],
        children: [],
      },
      {
        name: 'admin-dashboard.left-navigation.marketplace',
        key: 'marketplace',
        priority: 300,
        path: `https://www.peoplekeep.com/marketplace`,
        icon: 'far fa-store',
        disabled: false,
        newTab: true,
        disabledDescription: 'admin-dashboard.left-navigation.company-settings-tooltip',
        activeForOtherLinks: [],
        children: [],
      },
    ],
  },
  {
    name: 'admin-dashboard.left-navigation.action-needed',
    key: ROUTE_PATHS.ACTION_NEEDED,
    priority: 3,
    path: ROUTE_PATHS.ACTION_NEEDED,
    children: [],
    icon: 'far fa-paper-plane',
    disabled: false,
    component: ActionNeeded,
    disabledDescription: '',
    activeForOtherLinks: [],
  },
  {
    name: 'admin-dashboard.left-navigation.dashboard',
    key: ROUTE_PATHS.ADMIN_DASHBOARD_PATH,
    priority: 1,
    path: ROUTE_PATHS.ADMIN_DASHBOARD_PATH,
    children: [],
    icon: 'far fa-tachometer',
    disabled: false,
    component: AdminDashboardHome,
    disabledDescription: '',
    activeForOtherLinks: [],
  },
]

export const findNavigationMenuByPath = (
  navigationMenu: MenuItem[],
  pathname: string,
): MenuItem | [MenuItem, MenuItem] | null => {
  for (let i = 0; i < navigationMenu.length; i++) {
    const parent = navigationMenu[i]

    if (isActiveForOtherLinks(parent.activeForOtherLinks, pathname)) return parent

    if (parent.path === pathname) return parent

    for (let j = 0; j < parent.children.length; j++) {
      const child = parent.children[j]
      if (isActiveForOtherLinks(child.activeForOtherLinks, pathname)) {
        if (child.children.length > 0)
          return [child.children.find((nestedChild) => nestedChild.path === pathname), child]
        return child
      }

      if (child.path === pathname) return child
    }
  }

  return null
}

const isActiveForOtherLinks = (listToSearch, pathname) => {
  for (let j = 0; j < listToSearch.length; j++) {
    const item = listToSearch[j]
    if (item === pathname) {
      return true
    }
  }
  return false
}

const ensureDependentTasksAreCompleted = (tasks: AdminTask[], taskIds: number[]): boolean => {
  let status: boolean = true
  for (let i = 0; i < tasks.length; i++) {
    if (taskIds.includes(tasks[i].id)) {
      if (tasks[i].status === TaskStatus.Done) {
        status = true
      } else {
        return false
      }
    } else {
      status = true
    }
  }
  return status
}
